import { A } from '@adornis/base/env-info.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import type { Document } from 'mongodb';
import { BHFilter } from '../entities/bh-filter.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { printComponent } from '../print-with-user-session.js';
import { mongo } from '../server/mongo.js';
import { fillInEmpty, lookupEntityById } from '../util.js';

export async function participationPreMatch(filter: BHFilter, states?: string[]): Promise<Document[]> {
  const stateFilter = states?.length ? { state: { $in: states } } : {};
  if (!filter.institutionID && !filter.timeframeID) return states?.length ? [{ $match: stateFilter }] : [];

  const products = await mongo()
    .collection<Product>(Product._collectionName)
    .find(
      {
        ...(filter.institutionID ? { institutionID: filter.institutionID } : {}),
        ...(filter.timeframeID ? { timeframeID: filter.timeframeID } : {}),
      },
      { projection: { _id: 1 } },
    )
    .toArray();

  // Restrict participations before the joins; the later match still applies the complete filter.
  return [{ $match: { productID: { $in: products.map(product => product._id) }, ...stateFilter } }];
}

export const coreParticipationAggregation: Document[] = [
  // join user
  { $lookup: lookupEntityById(User, 'userID', 'user') },
  { $unwind: '$user' },

  // join product
  { $lookup: lookupEntityById(Workshop, 'productID', 'product') },
  { $unwind: '$product' },

  {
    $addFields: {
      institutionID: '$product.institutionID', // to match bh-filter
      timeframeID: '$product.timeframeID', // to match bh-filter
      'product.category._id': '$product.categoryID', // to match bh-filter
    },
  },

  // add fields
  { $addFields: { isNoShow: { $and: [{ $eq: ['$state', ParticipationState.in] }] } } },
];

export const stammdatenParticipationAggregation: Document[] = [
  // join userStammdaten
  {
    $lookup: {
      // join all user stammdaten
      from: UserStammdaten._collectionName,
      foreignField: 'userID',
      localField: 'userID',
      as: 'userStammdaten',
    },
  },
  {
    $addFields: {
      // only keep institution stammdaten
      userStammdaten: {
        $filter: {
          input: '$userStammdaten',
          as: 'stammdaten',
          cond: { $eq: ['$$stammdaten.institutionID', '$product.institutionID'] },
        },
      },
    },
  },
  { $set: { userStammdaten: { $first: '$userStammdaten' } } },
];

export const baseParticipationAggregation: Document[] = [
  ...coreParticipationAggregation,
  ...stammdatenParticipationAggregation,
];

export function statisticParticipationAggregation(filter: BHFilter): Document[] {
  // Only join stammdaten when the statistics filter needs them.
  return JSON.stringify(filter.getMongoFilter()).includes('userStammdaten')
    ? [...coreParticipationAggregation, ...stammdatenParticipationAggregation]
    : [...coreParticipationAggregation];
}

export type StatisticFilterClassification =
  { fast: false } | { fast: true; productQuery: Document; rawClauses: Document[]; stateClauses: Document[] };

export function classifyStatisticFilter(filter: BHFilter): StatisticFilterClassification {
  const mongoFilter = filter.getMongoFilter();
  const productClauses: Document[] = [];
  const rawClauses: Document[] = [];
  const stateClauses: Document[] = [];
  const clauses: Array<{ clause: Document; topLevel: boolean }> = [];

  for (const [key, value] of Object.entries(mongoFilter)) {
    if (key === '$and') {
      if (!Array.isArray(value) || !value.length) return { fast: false };
      clauses.push(...value.map(clause => ({ clause, topLevel: false })));
    } else clauses.push({ clause: { [key]: value }, topLevel: true });
  }

  const collectPaths = (clause: Document): string[] | null => {
    if (!clause || typeof clause !== 'object' || Array.isArray(clause)) return null;
    const paths: string[] = [];
    for (const [key, value] of Object.entries(clause)) {
      if (!key.startsWith('$')) paths.push(key);
      else if (['$and', '$or', '$nor'].includes(key) && Array.isArray(value) && value.length) {
        for (const nested of value) {
          const nestedPaths = collectPaths(nested);
          if (!nestedPaths?.length) return null;
          paths.push(...nestedPaths);
        }
      } else return null;
    }
    return paths;
  };

  const translateProductClause = (clause: Document): Document => ({
    $and: Object.entries(clause).map(([key, value]) => {
      if (key.startsWith('$')) return { [key]: value.map(translateProductClause) };
      const path = key.slice('product.'.length);
      return { [path === 'category._id' ? 'categoryID' : path]: value };
    }),
  });

  for (const { clause, topLevel } of clauses) {
    const paths = collectPaths(clause);
    if (!paths?.length) return { fast: false };
    if (paths.every(path => path.startsWith('product.'))) {
      productClauses.push(translateProductClause(clause));
      continue;
    }
    if (
      topLevel &&
      paths.length === 1 &&
      ['institutionID', 'timeframeID'].includes(paths[0]!) &&
      typeof clause[paths[0]!] === 'string'
    ) {
      productClauses.push(clause);
      continue;
    }
    if (paths.includes('isNoShow')) {
      if (Object.keys(clause).length !== 1 || !Object.hasOwn(clause, 'isNoShow')) return { fast: false };
      const condition = clause.isNoShow;
      const value =
        typeof condition === 'boolean'
          ? condition
          : condition && Object.keys(condition).length === 1 && typeof condition.$eq === 'boolean'
            ? condition.$eq
            : undefined;
      if (value === undefined) return { fast: false };
      stateClauses.push({ state: value ? ParticipationState.in : { $ne: ParticipationState.in } });
      continue;
    }
    if (
      paths.some(
        path =>
          path.startsWith('user') ||
          path === 'product' ||
          path.includes('.') ||
          ['institutionID', 'timeframeID'].includes(path),
      )
    )
      return { fast: false };
    rawClauses.push(clause);
  }

  return {
    fast: true,
    productQuery: productClauses.length ? { $and: productClauses } : {},
    rawClauses,
    stateClauses,
  };
}

async function runStatisticQuery<T>(
  operationName: string,
  filter: BHFilter,
  tail: Document[],
  postProcess: (result: Document[]) => T,
  options: {
    states?: string[];
    matchFields?: Document;
    fastTail?: Document[];
    limit?: number;
  } = {},
): Promise<T> {
  const collection = mongo().collection(Participation._collectionName);
  const run = async (pipeline: Document[]) => {
    let cursor = collection.aggregate(pipeline);
    if (options.limit !== undefined) cursor = cursor.sort({ year: -1, week: -1 }).limit(options.limit);
    return postProcess(await cursor.toArray());
  };
  const slow = async () =>
    run([
      ...(await participationPreMatch(filter, options.states)),
      ...statisticParticipationAggregation(filter),
      { $match: { ...options.matchFields, ...filter.getMongoFilter() } },
      ...tail,
    ]);
  const classification = classifyStatisticFilter(filter);
  if (!classification.fast) return slow();

  const products = await mongo()
    .collection<Product>(Product._collectionName)
    .find(classification.productQuery, { projection: { _id: 1 } })
    .toArray();
  const baseClauses: Document[] = [
    { productID: { $in: products.map(product => product._id) } },
    ...(options.states?.length ? [{ state: { $in: options.states } }] : []),
    ...classification.rawClauses,
    ...classification.stateClauses,
  ];
  const orphanUsers = await collection
    .aggregate([
      { $match: { $and: baseClauses } },
      { $group: { _id: '$userID' } },
      {
        $lookup: {
          from: User._collectionName,
          localField: '_id',
          foreignField: '_id',
          pipeline: [{ $project: { _id: 1 } }],
          as: 'u',
        },
      },
      { $match: { u: { $size: 0 } } },
    ])
    .toArray();
  const fastResult = await run([
    { $match: { $and: [...baseClauses, { userID: { $ne: null, $nin: orphanUsers.map(user => user._id) } }] } },
    ...(options.fastTail ?? tail),
  ]);

  return fastResult;
}

export const getParticipantsCount = registerQuery({
  type: () => Int,
  operationName: 'getParticipantsCount',
  params: [{ type: () => BHFilter, name: 'filter' }],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      params.filter.institutionID
        ? user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID)
        : user.isSuperAdmin,
    ),
  ],
  resolve: (filter: BHFilter) => {
    return async () => {
      const tail = [
        // count distinct users
        { $group: { _id: '$userID' } },
        { $count: 'total' },
      ];

      return runStatisticQuery('getParticipantsCount', filter, tail, result => result[0]?.total ?? 0);
    };
  },
});

export const getParticipationCount = registerQuery({
  type: () => Int,
  operationName: 'getParticipationCount',
  params: [
    { type: () => [String], name: 'states' },
    { type: () => BHFilter, name: 'filter' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      params.filter.institutionID
        ? user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID)
        : user.isSuperAdmin,
    ),
  ],
  resolve: (states: ParticipationState[], filter: BHFilter) => {
    return async () => {
      const stateFilter = (() => {
        if (states.length === 0) return {};
        return { state: { $in: states } };
      })();

      const tail = [{ $count: 'total' }];

      return runStatisticQuery('getParticipationCount', filter, tail, result => result[0]?.total ?? 0, {
        states,
        matchFields: stateFilter,
      });
    };
  },
});

export const getAverageParticipationsPerUser = registerQuery({
  type: () => Float,
  operationName: 'getAverageParticipationsPerUser',
  params: [
    { type: () => [String], name: 'states' },
    { type: () => BHFilter, name: 'filter' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      params.filter.institutionID
        ? user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID)
        : user.isSuperAdmin,
    ),
  ],
  resolve: (states: ParticipationState[], filter: BHFilter) => {
    return async () => {
      const doesMatchStateFilter = (() => {
        if (states.length === 0) return true;
        return { $in: ['$state', states] };
      })();

      const tail = [
        { $addFields: { doesMatchStateFilter: doesMatchStateFilter } },
        { $group: { _id: '$userID', total: { $sum: { $cond: ['$doesMatchStateFilter', 1, 0] } } } }, // count per user
        { $group: { _id: null, averageTotal: { $avg: '$total' } } }, // calculate average
      ];

      return runStatisticQuery('getAverageParticipationsPerUser', filter, tail, result => result[0]?.averageTotal ?? 0);
    };
  },
});

export const getUserProductRegistrationsOverTime = registerQuery({
  type: () => [[String, String, Int]],
  operationName: 'getUserProductRegistrationsOverTime',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => Int, name: 'limit' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      params.filter.institutionID
        ? user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID)
        : user.isSuperAdmin,
    ),
  ],
  resolve: (filter: BHFilter, limit = 10) => {
    return async () => {
      const tail = [
        { $project: { week: { $week: '$createdAt' }, year: { $year: '$createdAt' } } },
        {
          $group: {
            _id: { week: '$week', year: '$year' },
            total: { $sum: 1 },
            week: { $first: '$week' },
            year: { $first: '$year' },
          },
        },
        { $match: { week: { $type: 'number' }, year: { $type: 'number' } } },
      ];

      return runStatisticQuery(
        'getUserProductRegistrationsOverTime',
        filter,
        tail,
        result => {
          const registrationsOverTime = fillInEmpty(result as any);
          return registrationsOverTime.slice(-1 * limit).map(({ week, year, total }) => [week, year, total]);
        },
        { limit },
      );
    };
  },
});

export const getParticipationsOverTime = registerQuery({
  type: () => [[String, String, Int]],
  operationName: 'getParticipationsOverTime',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => Int, name: 'limit' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      params.filter.institutionID
        ? user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID)
        : user.isSuperAdmin,
    ),
  ],
  resolve: (filter: BHFilter, limit = 10) => {
    return async () => {
      const tail = [
        { $project: { week: { $week: '$updatedAt' }, year: { $year: '$updatedAt' } } },
        {
          $group: {
            _id: { week: '$week', year: '$year' },
            total: { $sum: 1 },
            week: { $first: '$week' },
            year: { $first: '$year' },
          },
        },
        { $match: { year: { $type: 'number' }, week: { $type: 'number' } } },
      ];

      return runStatisticQuery(
        'getParticipationsOverTime',
        filter,
        tail,
        result => {
          const registrationsOverTime = fillInEmpty(result as any);
          return registrationsOverTime.slice(-1 * limit).map(({ week, year, total }) => [week, year, total]);
        },
        { limit, states: [ParticipationState.participated], matchFields: { state: ParticipationState.participated } },
      );
    };
  },
});

@Entity()
export class TimeframeParticipationCountByStates extends AdornisEntity {
  public static override _class = 'TimeframeParticipationCountByStates';

  @Field(type => String)
  public timeframeID!: string;

  @DataLoadedField(type => Timeframe, doc => ({ _id: doc.timeframeID as any }))
  public timeframe!: Timeframe;

  @Field(type => [[String, Int]])
  public counts!: Array<[string, number]>;
}

export const getParticipationCountGroupedByTimeframeState = registerQuery({
  type: () => [TimeframeParticipationCountByStates],
  operationName: 'getParticipationCountGroupedByTimeframeState',
  params: [{ type: () => BHFilter, name: 'filter' }],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      params.filter.institutionID
        ? user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID)
        : user.isSuperAdmin,
    ),
  ],
  resolve: (filter: BHFilter) => {
    return async (gqlFields: BaseQLSelectionSet<TimeframeParticipationCountByStates>) => {
      const tail = [
        {
          $group: {
            _id: { timeframeID: '$product.timeframeID', state: '$state' },
            total: { $sum: 1 },
            timeframeID: { $first: '$product.timeframeID' },
            state: { $first: '$state' },
          },
        },
      ];

      const fastTail = [
        { $group: { _id: { productID: '$productID', state: '$state' }, total: { $sum: 1 } } },
        {
          $lookup: {
            from: Product._collectionName,
            localField: '_id.productID',
            foreignField: '_id',
            pipeline: [{ $project: { timeframeID: 1 } }],
            as: 'product',
          },
        },
        { $unwind: '$product' },
        {
          $group: {
            _id: { timeframeID: '$product.timeframeID', state: '$_id.state' },
            total: { $sum: '$total' },
            timeframeID: { $first: '$product.timeframeID' },
            state: { $first: '$_id.state' },
          },
        },
      ];

      return runStatisticQuery(
        'getParticipationCountGroupedByTimeframeState',
        filter,
        tail,
        result => {
          const counts = result.reduce((acc, r) => {
            const timeframeID = r.timeframeID;
            if (!timeframeID) return acc;
            const tpCount = acc[timeframeID] || new TimeframeParticipationCountByStates({ timeframeID, counts: [] });
            acc[timeframeID] = tpCount;
            tpCount.counts.push([r.state, r.total]);
            return acc;
          }, {});

          return Object.values(counts);
        },
        { fastTail },
      );
    };
  },
});

export const getUserCount = registerQuery({
  type: () => Int,
  operationName: 'getUserCount',
  params: [{ type: () => [String], name: 'userTypes' }],
  middlewares: [checkAuthorizedMiddleware(({ user }) => user.isAdmin)],
  resolve: (userTypes: string[]) => {
    return async () => {
      const aggregation = [
        { $match: { 'roles.name': { $in: userTypes } } }, //
        { $count: 'total' },
      ];

      const userCollection = mongo().collection(User._collectionName);
      const result = await userCollection.aggregate(aggregation).toArray();

      return result[0]?.total ?? 0;
    };
  },
});

export const generateStatisticPdf = registerMutation({
  type: () => String,
  operationName: 'generateeStatisicPdf',
  params: [{ type: () => BHFilter, name: 'filter' }],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      params.filter.institutionID
        ? user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID)
        : user.isSuperAdmin,
    ),
  ],
  resolve: (filter: BHFilter) => {
    return async () => {
      const user = await getCurrentUserServer();
      if (!user) throw new Error('user not found');

      const buffer = await printComponent('statistics-institution', {
        user,
        timeout: 25 * 1000,
        queryParams: { filter: btoa(JSON.stringify(filter.toJSON())) },
      });

      const institutionID = filter.institutionID ?? 'global';
      const name = `statistics-${institutionID}.pdf`;
      const file = await AdornisFile.createFromBuffer(buffer, name);

      return A.absoluteUrl(file.getServeLink() + '/' + name);
    };
  },
});
